<template>
	<div class="invite-verification">
		<h1>
			{{ $t('onboarding_verification_sent', { EmailPhone: '' }) }}
			<div class="contact" v-text="contactInfo" />
		</h1>

		<FormItem
			:label="$t('onboarding_verify_input_placeholder')"
			class="code-form"
			:error="!!error"
			:message="error"
		>
			<Input v-model="code" @enter="submit" large />
		</FormItem>

		<div class="verify-actions">
			<Checkbox v-model="persist" pronto>
				{{ $t('onboarding_keep_me_signed_in') }}
			</Checkbox>

			<Button class="resend-countdown" disabled link v-if="seconds > 0">
				{{ $t('onboarding_verify_resend_available_time', { Time: time }) }}
			</Button>
			<Button
				class="resend-btn"
				link
				pronto
				:loading="isSendingCode"
				v-if="seconds <= 0"
				@click="resendCode"
			>
				{{ $t('onboarding_verify_button_resend') }}
			</Button>
		</div>

		<Button
			pronto
			large
			block
			@click="submit"
			class="verify-btn"
			:disabled="!code"
		>
			{{ $t('onboarding_splash_signin') }}
		</Button>
	</div>
</template>

<script>
import { FormItem, Input, Button, Checkbox } from '@/components/common'
import { post } from '@/helpers/api'
import {
	getInvite,
	getUserData,
	setUserData,
	clearInviteData,
	sendCodeToExistingUser,
	sendCodeToNewUser,
	newAccountsRoute,
} from './index'
import { redirectToAccount, getDeviceInfo } from '@/helperMethods'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { inviteAccessTypes } from '@/helpers/constants'

const RETRY_DELAY_SEC = 30

export default {
	name: 'Verification',
	components: {
		FormItem,
		Input,
		Button,
		Checkbox,
	},

	data() {
		return {
			invite: getInvite(),
			user: getUserData(),
			code: null,
			persist: false,
			seconds: RETRY_DELAY_SEC,
			isLoading: false,
			isSendingCode: false,
			error: null,
		}
	},

	mounted() {
		this.startCountdown()
	},

	computed: {
		contactInfo() {
			return this.user.phone || this.user.email
		},

		disabled() {
			return !this.code
		},

		time() {
			if (this.seconds < 10) {
				return `0:0${this.seconds}`
			}
			return `0:${this.seconds}`
		},
	},

	methods: {
		async resendCode() {
			try {
				this.isSendingCode = true
				const { phone, isExistingUser } = getUserData()
				const body = { [phone ? 'phone' : 'email']: this.contactInfo }
				if (isExistingUser) {
					await sendCodeToExistingUser(body)
				} else {
					await sendCodeToNewUser(body)
				}
				this.$alert.success(this.$t('common_confirmation_sent_nocheck'))
				this.startCountdown()
			} catch (e) {
				console.error(e)
				this.$alert.error()
			}
			this.isSendingCode = false
		},
		async submit() {
			this.error = null
			this.isLoading = true
			const { isExistingUser } = getUserData()
			if (isExistingUser) {
				await this.getLoginTokenForExistingUser()
			} else {
				await this.getLoginTokenForNewUser()
			}
			this.isLoading = false
		},
		async getLoginTokenForNewUser() {
			try {
				const invite = getInvite()
				const { firstname, lastname, email, phone } = getUserData()
				const { data } = await post(
					`/api/clients/invites/${invite.code}/logintoken`,
					{
						firstname,
						lastname,
						email,
						phone,
						verification_code: this.code,
					}
				)
				this.redirectToMainApp(invite, data.data.login_token)
			} catch (e) {
				console.error(e)
			}
		},
		async getLoginTokenForExistingUser() {
			try {
				const { email, phone } = getUserData()
				let body = { email }
				if (phone) {
					const { number, country } = parsePhoneNumberFromString(phone)
					body = { phone: number, phone_country: country }
				}
				Object.assign(body, {
					code: this.code,
					device: getDeviceInfo(),
				})

				const invite = getInvite()
				const { data } = await post('/api/v3/user.login', body)

				const usersForOrg = data.users.filter(
					({ user }) => user.organization.id === invite.organization.id
				)

				if (!usersForOrg.length) {
					// no accounts for this org
					this.error = this.$t('common_api_error')
					return
				}

				if (usersForOrg.length === 1) {
					// if only one account, sign in with that one
					const [{ logintoken, user }] = usersForOrg
					this.redirectToMainApp(user, logintoken)
					return
				}

				// if multiple accounts, move to the account picker
				setUserData({ accounts: usersForOrg })
				this.$router.push({
					name: newAccountsRoute.name,
					query: { persist: this.persist },
					params: this.$route.params,
				})
			} catch (error) {
				console.error(error)
				switch (error.response?.data?.error) {
					case 'MISSING_CODE':
					case 'INVALID_LENGTH_CODE':
					case 'INCORRECT_CODE':
						this.error = this.$t('apierrors_INCORRECT_CODE')
						break
					default:
						this.error = this.$t('common_api_error')
						break
				}
			}
		},
		startCountdown() {
			this.seconds = RETRY_DELAY_SEC
			this.stopTimer = setInterval(() => {
				if (this.seconds <= 0) {
					clearInterval(this.stopTimer)
					this.seconds = 0
					return
				}
				this.seconds--
			}, 1000)
		},
		redirectToMainApp(user, logintoken) {
			const invite = getInvite()
			const params = { persist: this.persist }
			if (invite.access === inviteAccessTypes.INTERNAL) {
				params.invite = invite.code
			}
			redirectToAccount(user, logintoken, params)
			clearInviteData()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.invite-verification {
	@include flex-column-center;

	> h1 {
		text-align: center;
		margin-bottom: 16px;
		margin: 0 0 55px 0;
		max-width: 100%;
		.contact {
			@include ellipsis;
		}
	}

	.code-form {
		align-self: stretch;
		margin-bottom: 8px;
		.pronto-input {
			background: white;
		}
	}

	.resend-countdown {
		opacity: 1;
		font-size: 16px;
		color: var(--neutral-400);
		padding-right: 0;
	}
	.resend-btn {
		font-size: 16px;
		padding-right: 0;
	}

	.verify-actions {
		@include flex-spread;
		align-self: stretch;
		.pronto-checkbox {
			color: var(--neutral-400);
		}
	}

	.verify-btn {
		display: flex;
		margin: 110px 0 16px 0;
	}
}
</style>
